import { useConfirm } from '@/components/Confirm';
import {
  type AccountBookingStatus,
  createBookingStatus,
  deactivateBookingStatus,
  deleteBookingStatusPermanently,
  listBookingStatuses,
  updateBookingStatus,
} from '@/lib/api';
import { Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

const PARENT_BUILT_IN_VALUES = [
  'pending',
  'confirmed',
  'checked_in',
  'checked_out',
  'cancelled',
  'no_show',
] as const;

type ParentBuiltIn = (typeof PARENT_BUILT_IN_VALUES)[number];

const PARENT_LABELS: Record<ParentBuiltIn, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  checked_in: 'Checked in',
  checked_out: 'Checked out',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

export function BookingStatusesPage() {
  const [rows, setRows] = useState<AccountBookingStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newParent, setNewParent] = useState<ParentBuiltIn>('confirmed');
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#004AAD');
  const { confirm, ConfirmDialog } = useConfirm();

  async function refresh() {
    setLoading(true);
    try {
      const fresh = await listBookingStatuses();
      setRows(fresh);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    try {
      await createBookingStatus({
        parentBuiltIn: newParent,
        displayName: newName.trim(),
        color: newColor,
      });
      setNewName('');
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const byParent = new Map<ParentBuiltIn, AccountBookingStatus[]>();
  for (const p of PARENT_BUILT_IN_VALUES) byParent.set(p, []);
  for (const row of rows) {
    const arr = byParent.get(row.parentBuiltIn as ParentBuiltIn);
    if (arr) arr.push(row);
  }

  return (
    <div className="space-y-4">
      <ConfirmDialog />
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight">Booking statuses</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Decorate the built-in booking lifecycle with tenant-defined display labels. Each child
          status maps to one built-in status — when the booking transitions to a different built-in,
          the child label is cleared automatically.
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <form onSubmit={handleAdd} className="mb-8 rounded-xl border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium">Add a status</h2>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Parent</span>
            <select
              value={newParent}
              onChange={(e) => setNewParent(e.target.value as ParentBuiltIn)}
              className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
            >
              {PARENT_BUILT_IN_VALUES.map((p) => (
                <option key={p} value={p}>
                  {PARENT_LABELS[p]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Display name</span>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. Awaiting deposit"
              className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-muted-foreground">Colour</span>
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="h-9 w-12 rounded-md border border-border bg-surface"
            />
          </label>
          <button
            type="submit"
            className="h-9 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Add
          </button>
        </div>
      </form>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {!loading && (
        <div className="space-y-6">
          {PARENT_BUILT_IN_VALUES.map((p) => {
            const list = byParent.get(p) ?? [];
            if (list.length === 0) return null;
            return (
              <section key={p}>
                <h2 className="mb-2 text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  {PARENT_LABELS[p]}
                </h2>
                <ul className="space-y-1">
                  {list.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-center justify-between rounded-md border border-border bg-surface px-3 py-2"
                    >
                      <div className="flex items-center gap-3">
                        {row.color && (
                          <span
                            aria-hidden
                            className="inline-block size-3 rounded-full"
                            style={{ backgroundColor: row.color }}
                          />
                        )}
                        <span className="text-sm">{row.displayName}</span>
                        {row.active === 0 && (
                          <span className="text-xs text-muted-foreground">(inactive)</span>
                        )}
                      </div>
                      <div className="flex gap-2 text-xs">
                        {row.active === 1 && (
                          <button
                            type="button"
                            onClick={async () => {
                              const next = prompt('New name', row.displayName);
                              if (next?.trim()) {
                                await updateBookingStatus(row.id, { displayName: next.trim() });
                                void refresh();
                              }
                            }}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            Edit
                          </button>
                        )}
                        {row.active === 1 ? (
                          <button
                            type="button"
                            onClick={async () => {
                              const yes = await confirm({
                                title: `Deactivate "${row.displayName}"?`,
                                message:
                                  'The status will no longer appear in pickers. You can delete it permanently afterwards.',
                                confirmLabel: 'Deactivate',
                                danger: true,
                              });
                              if (!yes) return;
                              try {
                                await deactivateBookingStatus(row.id);
                                void refresh();
                              } catch (err) {
                                setError((err as Error).message);
                              }
                            }}
                            className="text-destructive hover:underline"
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={async () => {
                              const yes = await confirm({
                                title: `Delete "${row.displayName}" permanently?`,
                                message:
                                  'This will permanently remove the status. This cannot be undone.',
                                confirmLabel: 'Delete permanently',
                                danger: true,
                              });
                              if (!yes) return;
                              try {
                                await deleteBookingStatusPermanently(row.id);
                                void refresh();
                              } catch (err) {
                                setError((err as Error).message);
                              }
                            }}
                            className="flex items-center gap-1 text-destructive hover:underline"
                          >
                            <Trash2 className="size-3" />
                            Delete
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
